import { createApi } from '@reduxjs/toolkit/query/react';
import { graphqlRequestBaseQuery } from '@rtk-query/graphql-request-base-query';
import { client } from 'graphql/client';
import { archiveMissionV2, cancelMissionV2, createMissionV2, resetMissionV2, startMissionV2 } from 'graphql/mutations';
import { getMissionV2 } from 'graphql/queries';
import { onUpdateMissionV2 } from 'graphql/subscriptions';
import { SafeMissionV2Schema } from 'helper/v2Schemas';

import { AppError } from './errors';
import { fetchV2 } from './utils';

import type {
    ArchiveMissionInput,
    ArchiveMissionV2Mutation,
    CancelMissionInput,
    CancelMissionV2Mutation,
    CreateMissionInputV2,
    CreateMissionV2Mutation,
    GetMissionV2Query,
    MissionV2,
    ResetMissionInput,
    ResetMissionV2Mutation,
    StartMissionInput,
    StartMissionV2Mutation,
} from 'API';

/**
 * Extracts MissionV2 from a MissionOrError union result.
 * On the ErrorResponse branch it throws an AppError carrying the backend
 * `errorCode` and `statusCode`, so callers can react to specific error cases.
 *
 * Note: the generated operations do not select `__typename` (typenameIntrospection
 * is disabled), so the ErrorResponse branch is detected by the presence of
 * `errorCode` rather than by `__typename`.
 */
const extractMission = (
    result: { __typename?: string; errorCode?: string; statusCode?: number } | null | undefined
): MissionV2 => {
    const isError = !result || result.__typename === 'ErrorResponse' || result.errorCode !== undefined;
    if (isError) {
        throw new AppError(result?.errorCode ?? 'Mission not found', {
            errorCode: result?.errorCode,
            statusCode: result?.statusCode,
        });
    }
    return result as unknown as MissionV2;
};

/** Parses a single MissionV2 through SafeMissionV2Schema for graceful degradation. */
const parseMission = (data: MissionV2) => SafeMissionV2Schema().parse(data);

/** Fetches a V2 mission by ID and subscribes to real-time updates via onUpdateMissionV2. */
const getMissionV2QueryFn = (id: string) =>
    fetchV2(
        () => client.graphql({ query: getMissionV2, variables: { id } }),
        (data: GetMissionV2Query) => parseMission(extractMission(data.getMissionV2)),
        'Unexpected error occurred while fetching V2 mission'
    );

/** Creates (assigns) a V2 mission for a vehicle and returns the created mission. */
const createMissionV2MutationFn = (variables: CreateMissionInputV2) =>
    fetchV2(
        () => client.graphql({ query: createMissionV2, variables: { input: variables } }),
        (data: CreateMissionV2Mutation) => parseMission(extractMission(data.createMissionV2)),
        `Unexpected error occurred while creating the mission with vin: ${variables.vin} and predefinedRouteId ${variables.predefinedRouteId}`
    );

/** Starts a V2 mission by ID and returns the updated mission. */
const startMissionV2MutationFn = (variables: StartMissionInput) =>
    fetchV2(
        () => client.graphql({ query: startMissionV2, variables: { input: variables } }),
        (data: StartMissionV2Mutation) => parseMission(extractMission(data.startMissionV2)),
        `Unexpected error occurred while starting the mission with id: ${variables.missionId}`
    );

/** Cancels a V2 mission by ID and returns the canceled mission. */
const cancelMissionV2MutationFn = (variables: CancelMissionInput) =>
    fetchV2(
        () => client.graphql({ query: cancelMissionV2, variables: { input: variables } }),
        (data: CancelMissionV2Mutation) => parseMission(extractMission(data.cancelMissionV2)),
        `Unexpected error occurred while canceling the mission with id: ${variables.missionId}`
    );

/** Archives a V2 mission by ID and returns the archived mission. */
const archiveMissionV2MutationFn = (variables: ArchiveMissionInput) =>
    fetchV2(
        () => client.graphql({ query: archiveMissionV2, variables: { input: variables } }),
        (data: ArchiveMissionV2Mutation) => parseMission(extractMission(data.archiveMissionV2)),
        `Unexpected error occurred while archiving the mission with id: ${variables.missionId}`
    );

/** Resets a V2 mission by ID and returns the reset mission. */
const resetMissionV2MutationFn = (variables: ResetMissionInput) =>
    fetchV2(
        () => client.graphql({ query: resetMissionV2, variables: { input: variables } }),
        (data: ResetMissionV2Mutation) => parseMission(extractMission(data.resetMissionV2)),
        `Unexpected error occurred while resetting the mission with id: ${variables.missionId}`
    );

export const missionV2Api = createApi({
    reducerPath: 'missionV2',
    baseQuery: graphqlRequestBaseQuery({ url: '/graphql' }),
    refetchOnMountOrArgChange: true,
    endpoints: builder => ({
        onMissionV2Subscription: builder.query<MissionV2, string>({
            queryFn: getMissionV2QueryFn,
            onCacheEntryAdded: async (id, { cacheDataLoaded, cacheEntryRemoved, updateCachedData }) => {
                const updateCache = (data: MissionV2) => {
                    updateCachedData(() => parseMission(data));
                };

                await cacheDataLoaded;
                if (import.meta.env.DEV) {
                    // mockSubscription({
                    //   timeOutMs: 10_000,
                    //   maxIterations: 10,
                    //   mockFn: mockMission,
                    //   callback: (data) => {
                    //     const updatedMission = MissionSchema().parse(data.onUpdateMission)
                    //     updateCache(updatedMission)
                    //   }
                    // })
                    // registerMissionsDevTools((data) => {
                    //   updateCache(data.onMissionUpdate)
                    // })
                    await cacheEntryRemoved;
                } else {
                    const connection = client.graphql({ query: onUpdateMissionV2, variables: { id } }).subscribe({
                        next: ({ data }) => {
                            if (!data.onUpdateMissionV2) {
                                console.error(
                                    '[onMissionV2Subscription]: Please make sure all the attributes are returned in the mutation'
                                );
                                return;
                            }
                            updateCache(data.onUpdateMissionV2);
                        },
                        error: error => console.warn(error),
                    });
                    cacheEntryRemoved
                        .then(() => {
                            connection.unsubscribe();
                        })
                        .catch(error => console.error(error));
                }
            },
        }),
        createMissionV2: builder.mutation<MissionV2, CreateMissionInputV2>({
            queryFn: createMissionV2MutationFn,
        }),
        startMissionV2: builder.mutation<MissionV2, StartMissionInput>({
            queryFn: startMissionV2MutationFn,
        }),
        cancelMissionV2: builder.mutation<MissionV2, CancelMissionInput>({
            queryFn: cancelMissionV2MutationFn,
        }),
        archiveMissionV2: builder.mutation<MissionV2, ArchiveMissionInput>({
            queryFn: archiveMissionV2MutationFn,
        }),
        resetMissionV2: builder.mutation<MissionV2, ResetMissionInput>({
            queryFn: resetMissionV2MutationFn,
        }),
    }),
});

export const {
    useOnMissionV2SubscriptionQuery,
    useCreateMissionV2Mutation,
    useStartMissionV2Mutation,
    useCancelMissionV2Mutation,
    useArchiveMissionV2Mutation,
    useResetMissionV2Mutation,
} = missionV2Api;
